// Playlists follow the account (Sep 2026, migrations 0012–0014): keep every
// playlist on this device in step with its one copy on the server, which your
// other devices, and everyone holding its link while it is shared, edit too.
//
// Signed in, a playlist gets its row when it is made (private, link_access
// 'none'), and a browser's playlists move into the account the first time it
// signs in; sharing is a setting on the row. Deleting one of your playlists
// deletes it from the account, so it goes from every device; one deleted on
// another device goes from this one on the next run. Another account signing
// in on this device takes the list off it rather than moving it across
// (`claimPlaylistsFor`). Unconfigured builds have no accounts, and their
// playlists stay on the device as before.
//
// Local-first, like account sync: the store stays what the app reads, so a
// playlist opens and plays offline, and an edit made offline goes up when the
// device is back. The engine runs a moment after you change a playlist, every
// few seconds while the app is on screen (asking only which rows changed, then
// fetching those), and when the app comes back into view. Each run sends
// pending deletions, gives new playlists their rows, then reconciles each
// linked playlist:
//
// * nothing changed on either side → nothing to do;
// * only the server changed → take its copy;
// * only this device changed → save, naming the revision the edit was made
//   against; a CONFLICT (someone saved first) re-reads and goes round again;
// * both changed → merge by row (storage/playlistMerge.ts), then save.
//
// In a SHARED playlist your own saved tunes travel as account refs
// (sharedTunes.ts), shared from your account before the playlist that names
// them is saved; a private one keeps them as saved refs, which your other
// devices open from the account's copy of each tune. A view-only link takes
// the server's copy whatever this device holds. Someone else's playlist that
// no longer opens (unshared, deleted) is dropped to an ordinary one, which
// then becomes yours; one of YOURS that no longer opens was deleted on another
// device and goes from this one. Only signed-in users with access sync.
//
// Shared playlists FOLLOW THE ACCOUNT (migration 0013): opening a link records
// it on the server, and a device signing in opens every shared playlist this
// account opened or shared that it does not have yet (`restoreSharedPlaylists`).
// Deleting one here forgets it, so it does not come back on the next device.

import { useSyncExternalStore } from 'react';
import { getAuthState, subscribeAuth } from '@/auth/session';
import { useStore } from '@/state/store';
import type { PlaylistDoc } from '@/storage/playlists';
import type { LinkAccess, PlaylistLink } from '@/storage/playlistLinks';
import {
  contentKey,
  mergePlaylistContent,
  playlistContent,
  sameContent,
  type PlaylistContent,
} from '@/storage/playlistMerge';
import {
  createAccountPlaylist,
  deleteOwnPlaylist,
  findOwnShare,
  followSharedPlaylists,
  listFollowedShares,
  listOwnShares,
  openSharedPlaylists,
  playlistRevisions,
  saveSharedPlaylist,
  setSharedPlaylistAccess,
  unfollowSharedPlaylist,
  type SharedPlaylistRow,
} from './sharedSetlists';
import { portableSetlist, publishWithTunes, shareTunes } from './sharedTunes';

/** How long an edit settles before it goes up. */
const PUSH_DELAY_MS = 1200;
/** How often an open app looks for other people's changes. */
const POLL_MS = 8000;
/** Save attempts per run before giving up until the next one. */
const MAX_ROUNDS = 4;
/** How often an open app looks for playlists made or opened on another
 *  device (and a return to the app, if it has been this long). */
const RESTORE_EVERY_MS = 20_000;

// --- status ------------------------------------------------------------------

export interface SharedPlaylistSyncStatus {
  phase: 'idle' | 'syncing' | 'error';
  error: string | null;
}

const IDLE: SharedPlaylistSyncStatus = { phase: 'idle', error: null };
let status: SharedPlaylistSyncStatus = IDLE;
const listeners = new Set<() => void>();

function publish(next: SharedPlaylistSyncStatus) {
  if (next.phase === status.phase && next.error === status.error) return;
  status = next;
  for (const listener of listeners) listener();
}

export function getSharedPlaylistSyncStatus(): SharedPlaylistSyncStatus {
  return status;
}

export function useSharedPlaylistSyncStatus(): SharedPlaylistSyncStatus {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSharedPlaylistSyncStatus,
    getSharedPlaylistSyncStatus,
  );
}

// --- writing to the store -------------------------------------------------------

/** True while the engine writes, so the change it makes does not look like an
 *  edit that needs sending. */
let applying = false;

function sameLink(a: PlaylistLink | undefined, b: PlaylistLink): boolean {
  return (
    !!a &&
    a.shareId === b.shareId &&
    a.access === b.access &&
    a.mine === b.mine &&
    a.revision === b.revision &&
    contentKey(a.base) === contentKey(b.base)
  );
}

/** Write only what changed: an unchanged poll must not touch the store, or
 *  every few seconds would re-persist it and stamp it for the other tabs. */
function write(id: string, content: PlaylistContent | null, link: PlaylistLink | null) {
  const state = useStore.getState();
  const list = state.userPlaylists.find((p) => p.id === id);
  const current = state.playlistLinks[id];
  const patch: Parameters<typeof state.applySharedPlaylist>[1] = {};
  if (content && list && !sameContent(playlistContent(list), content)) patch.content = content;
  if (link === null ? current !== undefined : !sameLink(current, link)) patch.link = link;
  if (!('content' in patch) && !('link' in patch)) return;
  applying = true;
  try {
    state.applySharedPlaylist(id, patch);
  } finally {
    applying = false;
  }
}

function currentContent(id: string): PlaylistContent | null {
  const list = useStore.getState().userPlaylists.find((p) => p.id === id);
  return list ? playlistContent(list) : null;
}

/** Edits made here while a request was out are carried onto what came back. */
function rebase(id: string, started: PlaylistContent, target: PlaylistContent): PlaylistContent {
  const now = currentContent(id);
  if (!now || sameContent(now, started)) return target;
  return mergePlaylistContent(started, now, target);
}

// --- one playlist ----------------------------------------------------------------------

/** Bring one linked playlist in step. True when it needs another round. */
async function reconcile(
  id: string,
  link: PlaylistLink,
  row: SharedPlaylistRow | undefined,
  owner: string,
): Promise<boolean> {
  const state = useStore.getState();
  const list = state.userPlaylists.find((p) => p.id === id);
  if (list && !row && link.mine) {
    // One of yours that no longer opens: deleted from your account on
    // another device, so it goes from this one too.
    applying = true;
    try {
      state.forgetAccountPlaylist(id);
    } finally {
      applying = false;
    }
    return false;
  }
  if (!list || !row) {
    // Deleted here, or someone else's link no longer opens: it is an ordinary
    // playlist now (and, signed in, it becomes yours on the next run).
    write(id, null, null);
    return false;
  }
  if (!row.doc) return false;
  const remote = playlistContent(row.doc);
  const canEdit = row.mine || row.access === 'edit';
  // `access` is what the link allows; the owner edits whatever it says (`mine`).
  const linkAt = (base: PlaylistContent, revision: number, access = row.access): PlaylistLink => ({
    shareId: link.shareId,
    access,
    mine: row.mine,
    revision,
    base,
  });

  // A view-only link: the sharer's copy is the playlist.
  if (!canEdit) {
    write(id, remote, linkAt(remote, row.revision));
    return false;
  }

  const started = playlistContent(list);
  const changedHere = !sameContent(started, link.base);
  const changedThere = row.revision !== link.revision;
  const merged = !changedThere
    ? started
    : !changedHere
      ? remote
      : mergePlaylistContent(link.base, started, remote);
  // A private playlist keeps saved refs (your devices open them from your
  // account); a shared one carries them as account refs everyone can open.
  const shared = !(row.mine && row.access === 'none');
  const { doc, tunes } = shared
    ? portableSetlist({ ...list, ...merged }, state.savedCharts, owner)
    : { doc: { ...list, ...merged }, tunes: [] };
  const outgoing = playlistContent(doc);

  if (sameContent(outgoing, remote)) {
    write(id, rebase(id, started, remote), linkAt(remote, row.revision));
    return false;
  }

  if (tunes.length) await shareTunes(tunes);
  const result = await saveSharedPlaylist(
    link.shareId,
    { ...doc, updatedAt: new Date().toISOString() },
    row.revision,
  );
  switch (result.status) {
    case 'saved':
      write(id, rebase(id, started, outgoing), linkAt(outgoing, result.revision));
      return false;
    case 'conflict':
      // Someone saved first: read their version and merge again.
      return true;
    case 'view-only':
      write(id, remote, linkAt(remote, row.revision, 'view'));
      return false;
    case 'gone':
      write(id, null, null);
      return false;
  }
}

// --- a run ------------------------------------------------------------------------------

let running = false;
let again = false;

/** This device's playlists are the signed-in account's from now on. */
function claim(owner: string) {
  applying = true;
  try {
    useStore.getState().claimPlaylistsFor(owner);
  } finally {
    applying = false;
  }
}

/** Your playlists deleted here: delete them from your account. */
async function sendDeletes() {
  const pending = useStore.getState().playlistDeletes;
  if (pending.length === 0) return;
  const done: string[] = [];
  for (const d of pending) {
    await deleteOwnPlaylist(d.shareId);
    done.push(d.shareId);
  }
  applying = true;
  try {
    useStore.getState().clearPlaylistDeletes(done);
  } finally {
    applying = false;
  }
}

/** Give every playlist on this device that has no row one in your account,
 *  private. One the account already holds is opened and merged instead. */
async function uploadNew() {
  const state = useStore.getState();
  const unlinked = state.userPlaylists.filter((p) => !state.playlistLinks[p.id]);
  for (const list of unlinked) {
    const sent = playlistContent(list);
    const created = await createAccountPlaylist(list);
    if (created) {
      write(list.id, null, {
        shareId: created.shareId,
        access: 'none',
        mine: true,
        revision: created.revision,
        base: sent,
      });
      continue;
    }
    const shareId = await findOwnShare(list.id);
    const row = shareId ? (await openSharedPlaylists([shareId])).get(shareId) : undefined;
    if (!shareId || !row?.doc) continue;
    useStore.getState().adoptSharedPlaylist(row.doc, {
      shareId,
      access: row.access,
      mine: true,
      revision: row.revision,
    });
  }
}

/** Reconcile every playlist now. Safe to call at any time. */
export async function syncSharedPlaylistsNow(): Promise<void> {
  if (getAuthState().status !== 'active') return;
  if (running) {
    again = true;
    return;
  }
  running = true;
  try {
    const owner = getAuthState().userId;
    if (!owner) return;
    claim(owner);
    await sendDeletes();
    await uploadNew();
    for (let round = 0; round < MAX_ROUNDS; round++) {
      const links = useStore.getState().playlistLinks;
      const ids = Object.keys(links);
      if (ids.length === 0) break;
      if (status.phase !== 'syncing') publish({ phase: 'syncing', error: status.error });
      const asked = new Set(ids.map((id) => links[id].shareId));
      // Which changed, cheaply; the documents only for those.
      const revisions = await playlistRevisions([...asked]);
      const changed = ids
        .map((id) => links[id])
        .filter((link) => {
          const rev = revisions.get(link.shareId);
          return rev && rev.revision !== link.revision;
        })
        .map((link) => link.shareId);
      const rows = changed.length ? await openSharedPlaylists(changed) : new Map();
      // Before taking one of YOURS off this device as deleted, read your own
      // rows directly: a different path from the functions above, so a fault
      // there can never empty a device. One still listed waits for next time.
      const missingMine = ids.filter((id) => links[id].mine && !revisions.has(links[id].shareId));
      const stillThere = missingMine.length
        ? new Set((await listOwnShares()).map((own) => own.shareId))
        : new Set<string>();
      let more = false;
      for (const id of ids) {
        const link = useStore.getState().playlistLinks[id];
        // A link made since the request went out was not asked about.
        if (!link || !asked.has(link.shareId)) continue;
        if (link.mine && stillThere.has(link.shareId)) continue;
        const rev = revisions.get(link.shareId);
        const list = useStore.getState().userPlaylists.find((p) => p.id === id);
        // Unchanged on the server: its copy is the one this device last saw.
        const row: SharedPlaylistRow | undefined = !rev
          ? undefined
          : rev.revision === link.revision && list
            ? {
                shareId: link.shareId,
                doc: { ...list, ...link.base },
                access: rev.access,
                revision: rev.revision,
                mine: rev.mine,
                updatedAt: null,
              }
            : rows.get(link.shareId);
        if (await reconcile(id, link, row, owner)) more = true;
      }
      if (!more) break;
    }
    publish(IDLE);
  } catch (error) {
    publish({
      phase: 'error',
      error: error instanceof Error ? error.message : 'Could not reach the shared playlists.',
    });
  } finally {
    running = false;
    if (again) {
      again = false;
      schedule(0);
    }
  }
}

// --- when to sync ------------------------------------------------------------------------

let timer: ReturnType<typeof setTimeout> | null = null;

function schedule(delay: number) {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    void syncSharedPlaylistsNow();
  }, delay);
}

/** Look for changes now — the playlist page asks when it opens one. */
export function refreshSharedPlaylists(): void {
  schedule(0);
}

// --- sharing, opening, and changing a link ------------------------------------------

/**
 * Open a shared playlist from its link: it joins your playlists, linked, and
 * from then on edits go both ways. Returns its id, or null when the link does
 * not open (unshared, or you have no access).
 */
export async function openSharedPlaylistLink(shareId: string): Promise<string | null> {
  const row = (await openSharedPlaylists([shareId])).get(shareId);
  if (!row?.doc) return null;
  useStore.getState().adoptSharedPlaylist(row.doc, {
    shareId,
    access: row.access,
    mine: row.mine,
    revision: row.revision,
  });
  // Remember it on the account, so every device signed in to it finds it. A
  // failed record costs nothing now: the next restore records what is here.
  if (!row.mine) await followSharedPlaylists([shareId]).catch(() => undefined);
  schedule(0);
  return row.doc.id;
}

let restoring = false;
let lastRestore = 0;

/**
 * Bring this account's playlists — its own, and the shared ones it opened —
 * from its other devices onto this one: every own row and every followed
 * share this device lacks. With `backfill`, first records the shared playlists
 * this device already follows (links opened before follows existed). One of
 * yours deleted here and not yet deleted from the account is not brought
 * back; a followed one deleted here is forgotten on the account unless it was
 * opened again after it was deleted. Returns how many arrived.
 */
export async function restoreSharedPlaylists({
  backfill = false,
}: { backfill?: boolean } = {}): Promise<number> {
  if (!active() || restoring) return 0;
  const owner = getAuthState().userId;
  if (!owner) return 0;
  restoring = true;
  lastRestore = Date.now();
  try {
    claim(owner);
    if (backfill) {
      const theirs = Object.values(useStore.getState().playlistLinks)
        .filter((link) => !link.mine)
        .map((link) => link.shareId);
      await followSharedPlaylists(theirs).catch(() => undefined);
      // One deleted while that was out: its forget may have landed first.
      const still = new Set(Object.values(useStore.getState().playlistLinks).map((l) => l.shareId));
      for (const shareId of theirs) {
        if (!still.has(shareId)) await unfollowSharedPlaylist(shareId).catch(() => undefined);
      }
    }
    // No record (a database before 0013, or a bad moment) still restores your own.
    const [followed, own] = await Promise.all([
      listFollowedShares().catch(() => []),
      listOwnShares(),
    ]);
    const pending = new Set(useStore.getState().playlistDeletes.map((d) => d.shareId));
    const here = new Set(Object.values(useStore.getState().playlistLinks).map((l) => l.shareId));
    const followedAt = new Map(followed.map((f) => [f.shareId, f.followedAt] as const));
    const wanted = [
      ...new Set([
        ...followed.map((f) => f.shareId),
        ...own.filter((o) => !pending.has(o.shareId)).map((o) => o.shareId),
      ]),
    ].filter((id) => !here.has(id));
    if (wanted.length === 0) return 0;
    const rows = await openSharedPlaylists(wanted);
    let added = 0;
    for (const shareId of wanted) {
      const row = rows.get(shareId);
      if (!row?.doc) continue;
      const state = useStore.getState();
      if (Object.values(state.playlistLinks).some((l) => l.shareId === shareId)) continue;
      // Read now, not before the request: a delete made while it was out counts.
      if (row.mine) {
        if (state.playlistDeletes.some((d) => d.shareId === shareId)) continue;
      } else {
        const deleted = state.deletedPlaylists.find((t) => t.id === row.doc?.id)?.deletedAt;
        // Deleted here. A follow older than the deletion is one the delete
        // failed to forget; a newer one means it was opened again elsewhere.
        const since = followedAt.get(shareId);
        if (deleted && (!since || Date.parse(since) <= Date.parse(deleted))) {
          await unfollowSharedPlaylist(shareId).catch(() => undefined);
          continue;
        }
      }
      state.adoptSharedPlaylist(row.doc, {
        shareId,
        access: row.access,
        mine: row.mine,
        revision: row.revision,
      });
      added++;
    }
    return added;
  } catch {
    return 0;
  } finally {
    restoring = false;
  }
}

/** Turn sharing on: anyone with the link can edit until you choose otherwise.
 *  Your saved tunes in it go as account refs, shared from your account, on
 *  the sync that follows. */
export async function sharePlaylist(list: PlaylistDoc): Promise<void> {
  const link = useStore.getState().playlistLinks[list.id];
  if (link?.mine) {
    await setSharedPlaylistAccess(link.shareId, 'edit');
    const current = useStore.getState().playlistLinks[list.id];
    if (current) write(list.id, null, { ...current, access: 'edit' });
    await syncSharedPlaylistsNow();
    return;
  }
  // No row yet (made offline, or the first sync has not run): share it now.
  const { shareId, revision, access, doc } = await publishWithTunes(
    list,
    useStore.getState().savedCharts,
  );
  useStore.getState().adoptSharedPlaylist(doc, { shareId, access, mine: true, revision });
}

/** A playlist you shared from another device (or before links were live):
 *  pick its link back up. True when there was one. */
export async function followOwnShare(playlistId: string): Promise<boolean> {
  const shareId = await findOwnShare(playlistId);
  return shareId ? (await openSharedPlaylistLink(shareId)) !== null : false;
}

/** Choose what everyone holding the link may do. */
export async function setPlaylistLinkAccess(playlistId: string, access: LinkAccess): Promise<void> {
  const link = useStore.getState().playlistLinks[playlistId];
  if (!link?.mine) return;
  await setSharedPlaylistAccess(link.shareId, access);
  const current = useStore.getState().playlistLinks[playlistId];
  if (current)
    useStore.getState().applySharedPlaylist(playlistId, { link: { ...current, access } });
}

/** Turn sharing off: the playlist stays in your account, private. Everyone
 *  who opened the link keeps it as it stands, as a playlist of their own. */
export async function stopSharingPlaylist(playlistId: string): Promise<void> {
  const link = useStore.getState().playlistLinks[playlistId];
  if (!link?.mine) return;
  await setSharedPlaylistAccess(link.shareId, 'none');
  const current = useStore.getState().playlistLinks[playlistId];
  if (current) write(playlistId, null, { ...current, access: 'none' });
}

const hasPlaylists = () => {
  const s = useStore.getState();
  return (
    s.userPlaylists.length > 0 ||
    Object.keys(s.playlistLinks).length > 0 ||
    s.playlistDeletes.length > 0
  );
};
const active = () => getAuthState().status === 'active';
/** A return to the app looks for other devices' playlists if it has been
 *  away this long. */
const VISIBLE_RESTORE_MS = 5000;

/**
 * Keep linked playlists in step: when an account becomes active, a moment
 * after an edit, every few seconds while the app is visible, and when it
 * returns. Returns the cleanup.
 */
export function startSharedPlaylistSync(): () => void {
  let wasActive = false;
  const onAuth = () => {
    const now = active();
    if (now && !wasActive) {
      void restoreSharedPlaylists({ backfill: true }).finally(() => schedule(0));
    }
    if (!now && wasActive) publish(IDLE);
    wasActive = now;
  };
  const unsubscribeAuth = subscribeAuth(onAuth);
  onAuth();
  const unsubscribeStore = useStore.subscribe((next, prev) => {
    if (applying) return;
    if (
      next.userPlaylists === prev.userPlaylists &&
      next.playlistLinks === prev.playlistLinks &&
      next.playlistDeletes === prev.playlistDeletes
    ) {
      return;
    }
    // A shared playlist you opened, deleted here: forget it on the account.
    if (next.playlistLinks !== prev.playlistLinks && active()) {
      for (const [id, link] of Object.entries(prev.playlistLinks)) {
        if (link.mine || next.playlistLinks[id]) continue;
        if (next.deletedPlaylists.some((t) => t.id === id)) {
          void unfollowSharedPlaylist(link.shareId).catch(() => undefined);
        }
      }
    }
    if (active()) schedule(PUSH_DELAY_MS);
  });
  const poll = setInterval(() => {
    if (timer || !active()) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    if (Date.now() - lastRestore > RESTORE_EVERY_MS) {
      void restoreSharedPlaylists().finally(() => schedule(0));
    } else if (hasPlaylists()) void syncSharedPlaylistsNow();
  }, POLL_MS);
  const onVisible = () => {
    if (document.visibilityState !== 'visible' || !active()) return;
    if (Date.now() - lastRestore > VISIBLE_RESTORE_MS) {
      void restoreSharedPlaylists().finally(() => schedule(0));
    } else schedule(0);
  };
  document.addEventListener('visibilitychange', onVisible);
  return () => {
    unsubscribeAuth();
    unsubscribeStore();
    clearInterval(poll);
    document.removeEventListener('visibilitychange', onVisible);
    if (timer) clearTimeout(timer);
    timer = null;
  };
}

// --- test seams ----------------------------------------------------------------------

export function __resetSharedPlaylistSyncForTests(): void {
  status = IDLE;
  running = false;
  restoring = false;
  lastRestore = 0;
  again = false;
  applying = false;
  if (timer) clearTimeout(timer);
  timer = null;
}
